import {connect} from "react-redux";
import {getAlLDocuments, messageReset} from "../store/documents";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useEffect, useState, useCallback, useMemo, useRef} from "react";
import {getAllVerifyResponse} from "../store/logIn";
import {FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Skeleton} from "@mui/material";
import Header from "../blocks/header";
import Dashboard from "../blocks/dashboard";
import Toolbar from "@mui/material/Toolbar";
import * as React from "react";
import Box from "@mui/material/Box";
import Copyright from "../blocks/copyright";
import ReactPaginate from "react-paginate";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Button from "@mui/material/Button";
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from 'axios';

// ── API client ──
const apiClient = axios.create({
    baseURL: 'https://fx.softmakon.uz/api/',
    timeout: 15000,
});

// ── Smart cache: faqat 30 soniya saqlaydi, scan sahifasidan qaytganda tozalaydi ──
const requestCache = new Map();
const CACHE_DURATION = 30 * 1000; // 30 sekund (5 daqiqa emas!)

function getCached(key) {
    const cached = requestCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data;
    }
    requestCache.delete(key);
    return null;
}

function setCache(key, data) {
    requestCache.set(key, {data, timestamp: Date.now()});
}

// ── Debounce hook ──
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

// ── Loading skeleton ──
const TableSkeleton = () => (
    <table className="table table-hover table-responsive">
        <thead>
        <tr>
            <th scope="col">#</th>
            <th scope="col">Buyurtma kodi</th>
            <th scope="col">Partiya raqami</th>
            <th scope="col">Trek kod</th>
            <th scope="col">Turi</th>
            <th scope="col">Scan qilingan</th>
            <th scope="col">Scan qilinmagan</th>
            <th scope="col">Yetkazib berilmoqda</th>
            <th scope="col">Scan qilish</th>
        </tr>
        </thead>
        <tbody>
        {[...Array(10)].map((_, index) => (
            <tr key={index}>
                <td><Skeleton variant="text" width={30}/></td>
                <td><Skeleton variant="text" width={100}/></td>
                <td><Skeleton variant="text" width={80}/></td>
                <td><Skeleton variant="text" width={120}/></td>
                <td><Skeleton variant="rectangular" width={60} height={24}/></td>
                <td><Skeleton variant="text" width={40}/></td>
                <td><Skeleton variant="text" width={40}/></td>
                <td><Skeleton variant="text" width={40}/></td>
                <td><Skeleton variant="rectangular" width={80} height={36}/></td>
            </tr>
        ))}
        </tbody>
    </table>
);

function Documents(props) {
    const navigate = useNavigate()
    const params = useParams()
    const [searchParams] = useSearchParams()
    const isFirstLoad = useRef(true);

    // ── URL parametrlarni olish ──
    const {trackType, serialNumber} = useMemo(() => {
        let type = searchParams.get('type')
        const serial = params['str']
        if (!type) {
            if (searchParams.has('avia')) type = 'avia'
            else if (searchParams.has('fura')) type = 'fura'
            else if (searchParams.has('avia_tracking')) type = 'avia_tracking'
        }
        return {trackType: type, serialNumber: serial}
    }, [searchParams, params])

    // ── State ──
    const [scan, setScan] = useState(() => {
        const saved = localStorage.getItem('scan');
        return saved ? JSON.parse(saved) : 1;
    })
    const [documents, setDocuments] = useState([])
    const [combinedData, setCombinedData] = useState([])
    const [filteredDocuments, setFilteredDocuments] = useState(false)
    const [searchWord, setSearchWord] = useState('')
    const [itemsPerPage] = useState(20)
    const [itemOffset, setItemOffset] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // ── Scan track — Map orqali tez qidirish (O(1) vs O(n)) ──
    const [scanTrackMap, setScanTrackMap] = useState(new Map());

    const debouncedSearchWord = useDebounce(searchWord, 300)

    // ── Deduplicate ──
    const deduplicateByClientCode = useCallback((data) => {
        const seen = new Map();
        for (const item of data) {
            const existing = seen.get(item.code);
            if (!existing) {
                seen.set(item.code, item);
            } else if (item.place === 'china' && existing.place === 'uzb') {
                seen.set(item.code, item);
            }
        }
        return Array.from(seen.values());
    }, []);

    // ── Normalize functions ──
    const normalizeFura = useCallback((item) => ({
        code: item.clientCode || item.code,
        serial_number: item.serial_number,
        track_code: item.trackingNumber || item.track_code,
        place: item.location,
        type: 'fura',
        weight: item.weight,
        status: item.status,
        confirmed: item.confirmed,
        boxId: item.boxId,
        all_sum: item.all_sum,
        createdAt: item.createdAt
    }), []);

    const normalizeAvia = useCallback((item) => ({
        code: item.code,
        serial_number: item.serial_number,
        track_code: item.track_code,
        place: item.place || 'china',
        type: 'avia',
        already_come: item.already_come,
        create_date: item.create_date
    }), []);

    const normalizeAviaTracking = useCallback((item) => ({
        code: item.clientCode || item.code,
        serial_number: item.serial_number,
        track_code: item.trackingNumber || item.track_code,
        place: item.location || 'china',
        type: 'avia_tracking',
        weight: item.weight,
        status: item.status,
        confirmed: item.confirmed,
        box_number: item.box_number,
        product_title: item.product_title,
        all_sum: item.all_sum,
        createdAt: item.createdAt
    }), []);

    // ── ScanTrack Map'ni yaratish (barcha raw data'dan) ──
    const buildScanTrackMap = useCallback((rawItems) => {
        const map = new Map();
        for (const item of rawItems) {
            const key = `${item.code}-${item.serial_number || 'no-serial'}`;
            if (!map.has(key)) {
                map.set(key, {code: item.code, serial_number: item.serial_number, uzb: 0, china: 0, ontheway: 0});
            }
            const entry = map.get(key);
            if (item.place === 'uzb') entry.uzb++;
            else if (item.place === 'ontheway') entry.ontheway++;
            else entry.china++;
        }
        return map;
    }, []);

    // ══════════════════════════════════════════════════════════════════
    // ASOSIY DATA FETCHING — optimizatsiya qilingan
    // ══════════════════════════════════════════════════════════════════
    const fetchTrackingData = useCallback(async (forceRefresh = false) => {
        try {
            setError(null);

            const cacheKey = `tracking-${trackType}-${serialNumber}`;

            // 1) Keshdan ko'rsatish (tezkor)
            if (!forceRefresh) {
                const cached = getCached(cacheKey);
                if (cached) {
                    setCombinedData(cached.combined);
                    setScanTrackMap(cached.scanMap);
                    setLoading(false);
                    return;
                }
            }

            setLoading(true);

            let allRawItems = [];
            let combinedTrackingData = [];

            if (trackType && serialNumber) {
                // ── Bitta type + serial_number uchun ──
                // cat= parametri orqali server tomonida filter (kamroq data)
                const response = await apiClient.get('combined-tracking/', {
                    params: {
                        type: trackType,
                        cat: serialNumber,
                    }
                });

                const result = response.data;

                if (trackType === 'fura') {
                    allRawItems = result.map(normalizeFura);
                } else if (trackType === 'avia_tracking') {
                    allRawItems = result.map(normalizeAviaTracking);
                } else {
                    allRawItems = result.map(normalizeAvia);
                }

                combinedTrackingData = deduplicateByClientCode(allRawItems);

            } else if (trackType) {
                // ── Faqat type bo'yicha ──
                const response = await apiClient.get('combined-tracking/', {
                    params: {type: trackType}
                });

                const result = response.data;
                if (trackType === 'fura') {
                    allRawItems = result.map(normalizeFura);
                } else if (trackType === 'avia_tracking') {
                    allRawItems = result.map(normalizeAviaTracking);
                } else {
                    allRawItems = result.map(normalizeAvia);
                }

                combinedTrackingData = deduplicateByClientCode(allRawItems);

            } else {
                // ── Uchtalasi parallel (avia + fura + avia_tracking) ──
                const [aviaRes, furaRes, aviaTrackingRes] = await Promise.all([
                    apiClient.get('combined-tracking/', {params: {type: 'avia'}}),
                    apiClient.get('combined-tracking/', {params: {type: 'fura'}}),
                    apiClient.get('combined-tracking/', {params: {type: 'avia_tracking'}}),
                ]);

                const aviaItems = aviaRes.data.map(normalizeAvia);
                const furaItems = furaRes.data.map(normalizeFura);
                const aviaTrackingItems = aviaTrackingRes.data.map(normalizeAviaTracking);
                allRawItems = [...furaItems, ...aviaItems, ...aviaTrackingItems];

                if (serialNumber) {
                    allRawItems = allRawItems.filter(item =>
                        item.serial_number && item.serial_number.toLowerCase() === serialNumber.toLowerCase()
                    );
                }

                combinedTrackingData = deduplicateByClientCode(allRawItems);
            }

            // ScanTrack Map'ni yaratish
            const scanMap = buildScanTrackMap(allRawItems);

            // Keshga saqlash
            setCache(cacheKey, {combined: combinedTrackingData, scanMap});

            // State yangilash (bir marta)
            setCombinedData(combinedTrackingData);
            setScanTrackMap(scanMap);

        } catch (error) {
            console.error('Error fetching tracking data:', error);
            setError("Ma'lumotlarni yuklashda xatolik yuz berdi");
        } finally {
            setLoading(false);
        }
    }, [trackType, serialNumber, deduplicateByClientCode, normalizeFura, normalizeAvia, normalizeAviaTracking, buildScanTrackMap]);

    // ── localStorage ──
    useEffect(() => {
        localStorage.setItem("scan", JSON.stringify(scan));
    }, [scan]);

    // ── URL o'zgarganda reset ──
    useEffect(() => {
        setSearchWord('');
        setFilteredDocuments(false);
        setItemOffset(0);
    }, [trackType, serialNumber]);

    // ── Initial load + sahifaga qaytganda yangilash ──
    useEffect(() => {
        if (isFirstLoad.current) {
            // Birinchi yuklanish — keshdan ko'rsatadi (tez), keyin yangilaydi
            isFirstLoad.current = false;
            fetchTrackingData(false);
        } else {
            // Qaytib keldi — keshni tozalab yangisini oladi
            requestCache.clear();
            fetchTrackingData(true);
        }

        if (!trackType) {
            props.getAlLDocuments(params['str']);
        }
    }, [trackType, serialNumber]);

    // ── props.documents o'zgarganda ──
    useEffect(() => {
        if (!trackType && props.documents.length > 0) {
            setDocuments(deduplicateByClientCode(props.documents));
        } else {
            setDocuments([]);
        }
    }, [props.documents, trackType, deduplicateByClientCode]);

    // ── Scan natijalarini olish — Map orqali O(1) ──
    const getScanResult = useCallback((item) => {
        const key = `${item.code}-${item.serial_number || 'no-serial'}`;
        return scanTrackMap.get(key)?.uzb || 0;
    }, [scanTrackMap]);

    const getNoScanResult = useCallback((item) => {
        const key = `${item.code}-${item.serial_number || 'no-serial'}`;
        return scanTrackMap.get(key)?.china || 0;
    }, [scanTrackMap]);

    const getOnTheWayResult = useCallback((item) => {
        const key = `${item.code}-${item.serial_number || 'no-serial'}`;
        return scanTrackMap.get(key)?.ontheway || 0;
    }, [scanTrackMap]);

    // ── Sahifadagi elementlar ──
    const pageItems = useMemo(() => {
        return filteredDocuments || (trackType ? combinedData : [...documents, ...combinedData])
    }, [filteredDocuments, documents, combinedData, trackType]);

    const {currentItems, pageCount} = useMemo(() => {
        const endOffset = itemOffset + itemsPerPage;
        return {
            currentItems: pageItems.slice(itemOffset, endOffset),
            pageCount: Math.ceil(pageItems.length / itemsPerPage)
        };
    }, [pageItems, itemOffset, itemsPerPage]);

    const handlePageClick = useCallback((event) => {
        const newOffset = (event.selected * itemsPerPage) % pageItems.length;
        setItemOffset(newOffset);
    }, [itemsPerPage, pageItems.length]);

    // ── Navigation ──
    const toEdit = useCallback((e, data) => {
        e.currentTarget.blur();
        navigate(`/one-doc/${params['str']}/${data['code']}?type=${data['type'] || trackType}`);
    }, [navigate, params, trackType]);

    const goBack = useCallback((e) => {
        e.preventDefault();
        navigate('/track-type/');
    }, [navigate]);

    // ── Filter (search + scan filter) ──
    useEffect(() => {
        // Faqat filter kerak bo'lganda ishlaydi
        if (!debouncedSearchWord && (scan === 1 || scan === '1')) {
            setFilteredDocuments(false);
            return;
        }

        let allRawData = trackType ? [...combinedData] : [...documents, ...combinedData];

        // Search filter
        if (debouncedSearchWord) {
            const searchLower = debouncedSearchWord.toLowerCase();
            allRawData = allRawData.filter(item =>
                item.code.toLowerCase().includes(searchLower)
            );
        }

        // Scan filter — scanTrackMap orqali aniq sanash
        // BUG FIX: item.place === scan ishlatish NOTO'G'RI, chunki combinedData
        // allaqachon deduplicatelanган — bitta mijozda china itemlar bo'lsa ham
        // deduplicate qilingan item place='uzb' bo'lishi mumkin → filter 0 qaytaradi.
        // scanTrackMap esa har bir (code + serial_number) uchun uzb/china/ontheway
        // SANOLARINI saqlaydi → bu orqali filter qilinadi.
        if (scan !== 1 && scan !== '1') {
            allRawData = allRawData.filter(item => {
                const key = `${item.code}-${item.serial_number || 'no-serial'}`;
                const entry = scanTrackMap.get(key);
                if (!entry) {
                    // scanTrackMap da yo'q bo'lsa — item.place ga fallback
                    return item.place === scan;
                }
                if (scan === 'china')    return entry.china > 0;
                if (scan === 'uzb')      return entry.uzb > 0;
                if (scan === 'ontheway') return entry.ontheway > 0;
                return item.place === scan;
            });
        }

        setFilteredDocuments(deduplicateByClientCode(allRawData));
    }, [scan, debouncedSearchWord, documents, combinedData, trackType, deduplicateByClientCode, scanTrackMap]);

    // ── Enter key ──
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && filteredDocuments && filteredDocuments.length > 0) {
            navigate(`/one-doc/${params['str']}/${filteredDocuments[0]['code']}?type=${filteredDocuments[0]['type'] || trackType}`)
        }
    }, [filteredDocuments, navigate, params, trackType]);

    // ══════════════════════════════════════════════════════════════════
    // RENDER
    // ══════════════════════════════════════════════════════════════════
    return (
        <div>
            <div className={'page worker-page documents'}>
                <Header/>
                <Dashboard/>
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                    }}>
                    <Toolbar/>
                    <div className={'main'}>
                        <Button variant="outlined" startIcon={<ArrowBackIcon/>} onClick={goBack}>
                            Ortga
                        </Button>

                        {error && (
                            <div className="alert alert-danger mt-3" role="alert">
                                {error}
                            </div>
                        )}

                        <div className={'header-page'}>
                            <h2 className={'page-title'}>
                                {"Buyurtmalar"}
                                {trackType && serialNumber && (
                                    <span className="ms-2">
                                        <span className={`badge ${
                                            trackType === 'avia' ? 'bg-primary' :
                                                trackType === 'avia_tracking' ? 'bg-warning text-dark' :
                                                    'bg-success'
                                        } me-2`}>
                                            {trackType === 'avia' ? 'Avia' :
                                                trackType === 'avia_tracking' ? 'Avia Tracking' :
                                                    'Fura'}
                                        </span>
                                        <span className="badge bg-secondary">
                                            {serialNumber}
                                        </span>
                                    </span>
                                )}
                            </h2>
                        </div>

                        <div className={'filter-block'}>
                            <div>
                                <p>
                                    <span>{"Izlash"}:</span>
                                    <FormControl variant="outlined">
                                        <InputLabel htmlFor="search-input">{"Izlash"}</InputLabel>
                                        <OutlinedInput
                                            id="search-input"
                                            type={'text'}
                                            autoFocus
                                            onKeyDown={handleKeyDown}
                                            value={searchWord}
                                            onChange={(e) => setSearchWord(e.target.value)}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton aria-label="toggle search" edge="end">
                                                        <SearchIcon/>
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label={"Izlash"}
                                        />
                                    </FormControl>
                                </p>
                                <p>
                                    <span>{"Buyurtmalar"}:</span>
                                    <Box sx={{minWidth: 200}}>
                                        <FormControl fullWidth>
                                            <InputLabel id="filter-select-label">{"Filter"}</InputLabel>
                                            <Select
                                                labelId="filter-select-label"
                                                id="filter-select"
                                                value={scan}
                                                label={"Filter"}
                                                onChange={(e) => setScan(e.target.value)}
                                            >
                                                <MenuItem value={1}>{"Barchasi"}</MenuItem>
                                                <MenuItem value={'uzb'}>{"Scan qilingan"}</MenuItem>
                                                <MenuItem value={'china'}>{"Scan qilinmagan"}</MenuItem>
                                                <MenuItem value={'ontheway'}>{"Yetkazib berilmoqda"}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </p>
                            </div>
                        </div>

                        {loading ? (
                            <TableSkeleton/>
                        ) : currentItems.length ? (
                            <table className="table table-hover table-responsive">
                                <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">{"Buyurtma kodi"}</th>
                                    <th scope="col">{"Partiya raqami"}</th>
                                    <th scope="col">{"Trek kod"}</th>
                                    <th scope="col">{"Turi"}</th>
                                    <th scope="col">{"Scan qilingan"}</th>
                                    <th scope="col">{"Scan qilinmagan"}</th>
                                    <th scope="col">{"Yetkazib berilmoqda"}</th>
                                    <th scope="col">{"Scan qilish"}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentItems.map((item, index) => (
                                    <tr key={`${item.code}-${item.serial_number}-${index}`}>
                                        <th scope="row">{itemOffset + index + 1}</th>
                                        <td>{item.code}</td>
                                        <td>{item.serial_number}</td>
                                        <td>{item.track_code || item.trackingNumber || '-'}</td>
                                        <td>
                                            <span className={`badge ${
                                                (item.type || trackType) === 'avia' ? 'bg-primary' :
                                                    (item.type || trackType) === 'avia_tracking' ? 'bg-warning text-dark' :
                                                        'bg-success'
                                            }`}>
                                                {(item.type || trackType) === 'avia' ? 'Avia' :
                                                    (item.type || trackType) === 'avia_tracking' ? 'Avia Tracking' :
                                                        'Fura'}
                                            </span>
                                        </td>
                                        <td>{getScanResult(item)}</td>
                                        <td>{getNoScanResult(item)}</td>
                                        <td>{getOnTheWayResult(item)}</td>
                                        <td>
                                            <Button
                                                variant="contained"
                                                title={"O'zgartirish"}
                                                color={"success"}
                                                endIcon={<DocumentScannerIcon/>}
                                                onClick={(e) => toEdit(e, item)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center py-5">
                                <p className="text-muted">Ma'lumotlar topilmadi</p>
                            </div>
                        )}

                        {pageCount > 1 && !loading && (
                            <div className={'pagination'}>
                                <ReactPaginate
                                    breakLabel="..."
                                    nextLabel={<KeyboardArrowRightIcon/>}
                                    onPageChange={handlePageClick}
                                    pageRangeDisplayed={5}
                                    pageCount={pageCount}
                                    previousLabel={<KeyboardArrowLeftIcon/>}
                                    renderOnZeroPageCount={null}
                                />
                            </div>
                        )}
                    </div>
                    <Copyright/>
                </Box>
            </div>
        </div>
    )
}

export default connect(
    props => props.documents,
    {getAlLDocuments, getAllVerifyResponse, messageReset}
)(Documents)